import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import logo from '../assets/logo.png';

export const exportToExcel = (data, filename, sheetName = 'Sheet1', title = '') => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(data);
  
  // Set column widths
  const maxWidths = [];
  if (data.length > 0) {
    Object.keys(data[0]).forEach(key => {
      maxWidths.push({
        wch: Math.max(
          key.length,
          ...data.map(row => String(row[key] || '').length)
        )
      });
    });
  }
  worksheet['!cols'] = maxWidths;
  
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

export const exportToPDF = (data, filename, title, columns = null) => {
  const doc = new jsPDF();
  
  // Add logo
  try {
    doc.addImage(logo, 'PNG', 14, 10, 20, 20);
  } catch (e) {
    console.log('Logo not found, skipping');
  }
  
  // Add title
  doc.setFontSize(16);
  doc.setTextColor(118, 0, 188); // #7600bc
  doc.text(title, 40, 20);
  
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 40, 28);
  
  // Prepare table data
  let tableData = [];
  let headers = [];
  
  if (columns) {
    headers = columns.map(col => col.header);
    tableData = data.map(row => columns.map(col => row[col.dataKey]));
  } else if (data.length > 0) {
    headers = Object.keys(data[0]);
    tableData = data.map(row => Object.values(row));
  }
  
  doc.autoTable({
    head: [headers],
    body: tableData,
    startY: 35,
    theme: 'grid',
    headStyles: {
      fillColor: [118, 0, 188], // #7600bc
      textColor: 255,
      fontSize: 10,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9,
      textColor: [50, 50, 50]
    },
    alternateRowStyles: {
      fillColor: [245, 240, 250]
    }
  });
  
  doc.save(`${filename}.pdf`);
};

// Export payment report with study year grouping
export const exportPaymentReport = (payments, student, studyYears, filename) => {
  const doc = new jsPDF();

  const resolvePaymentTypeLabel = (payment) => {
    const rawType = payment.paymentTypeKey || payment.paymentType || 'oneTime';
    const labels = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
      oneTime: 'One-time',
      enrollmentFee: 'Enrollment fee'
    };
    return labels[rawType] || rawType;
  };

  const resolveYearLabel = (payment) => {
    if (payment.schoolYearLabel) {
      return payment.schoolYearLabel;
    }

    const studyYear = studyYears.find(sy => String(sy.id) === String(payment.studyYearId));
    if (studyYear) {
      return `Year ${studyYear.year}`;
    }

    return payment.schoolYear || 'Unknown school year';
  };
  
  try {
    doc.addImage(logo, 'PNG', 14, 10, 20, 20);
  } catch (e) {
    console.log('Logo not found');
  }
  
  doc.setFontSize(16);
  doc.setTextColor(118, 0, 188);
  doc.text('Payment Statement', 40, 20);
  
  doc.setFontSize(12);
  doc.setTextColor(50);
  doc.text(`Student: ${student.name}`, 14, 40);
  doc.text(`Email: ${student.email}`, 14, 48);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 56);
  
  let currentY = 65;
  
  // Group payments by school year label.
  const paymentsByYear = {};
  payments.forEach(payment => {
    const yearKey = resolveYearLabel(payment);
    if (!paymentsByYear[yearKey]) {
      paymentsByYear[yearKey] = [];
    }
    paymentsByYear[yearKey].push(payment);
  });
  
  Object.keys(paymentsByYear).forEach(yearKey => {
    doc.setFontSize(12);
    doc.setTextColor(118, 0, 188);
    doc.text(yearKey, 14, currentY);
    currentY += 5;
    
    const yearPayments = paymentsByYear[yearKey];
    const tableData = yearPayments.map(p => [
      resolvePaymentTypeLabel(p),
      `SRD ${p.amount.toFixed(2)}`,
      `SRD ${p.paidAmount.toFixed(2)}`,
      `SRD ${(p.amount - p.paidAmount).toFixed(2)}`,
      p.status
    ]);
    
    doc.autoTable({
      head: [['Payment Type', 'Total', 'Paid', 'Remaining', 'Status']],
      body: tableData,
      startY: currentY,
      theme: 'grid',
      headStyles: {
        fillColor: [118, 0, 188],
        textColor: 255,
        fontSize: 9
      },
      bodyStyles: {
        fontSize: 8
      }
    });
    
    currentY = doc.lastAutoTable.finalY + 15;
  });
  
  // Add totals
  const totalAmount = payments.reduce((sum, p) => sum + p.amount, 0);
  const totalPaid = payments.reduce((sum, p) => sum + p.paidAmount, 0);
  
  doc.setFontSize(11);
  doc.setTextColor(50);
  doc.text(`Total Amount: SRD ${totalAmount.toFixed(2)}`, 14, currentY);
  doc.text(`Total Paid: SRD ${totalPaid.toFixed(2)}`, 14, currentY + 8);
  doc.text(`Remaining: SRD ${(totalAmount - totalPaid).toFixed(2)}`, 14, currentY + 16);
  
  doc.save(`${filename}.pdf`);
};

// Export payment report to Excel
export const exportPaymentReportExcel = (payments, student, studyYears, filename) => {
  const workbook = XLSX.utils.book_new();

  const resolvePaymentTypeLabel = (payment) => {
    const rawType = payment.paymentTypeKey || payment.paymentType || 'oneTime';
    const labels = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
      oneTime: 'One-time',
      enrollmentFee: 'Enrollment fee'
    };
    return labels[rawType] || rawType;
  };

  const resolveYearLabel = (payment) => {
    if (payment.schoolYearLabel) {
      return payment.schoolYearLabel;
    }

    const studyYear = studyYears.find(sy => String(sy.id) === String(payment.studyYearId));
    if (studyYear) {
      return `Year ${studyYear.year}`;
    }

    return payment.schoolYear || 'Unknown school year';
  };
  
  // Summary sheet
  const summaryData = [
    ['Payment Statement'],
    [''],
    ['Student Name:', student.name],
    ['Student Email:', student.email],
    ['Generated:', new Date().toLocaleDateString()],
    [''],
    ['Total Amount:', `SRD ${payments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}`],
    ['Total Paid:', `SRD ${payments.reduce((sum, p) => sum + p.paidAmount, 0).toFixed(2)}`],
    ['Remaining:', `SRD ${(payments.reduce((sum, p) => sum + p.amount, 0) - payments.reduce((sum, p) => sum + p.paidAmount, 0)).toFixed(2)}`]
  ];
  
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
  
  // Group by school year label.
  const paymentsByYear = {};
  payments.forEach(payment => {
    const yearKey = resolveYearLabel(payment);
    if (!paymentsByYear[yearKey]) {
      paymentsByYear[yearKey] = [];
    }
    paymentsByYear[yearKey].push(payment);
  });
  
  // Create sheet for each year
  Object.keys(paymentsByYear).forEach(yearKey => {
    const yearPayments = paymentsByYear[yearKey];
    const sheetData = [
      ['Payment Type', 'Total Amount', 'Paid Amount', 'Remaining', 'Status'],
      ...yearPayments.map(p => [
        resolvePaymentTypeLabel(p),
        p.amount,
        p.paidAmount,
        p.amount - p.paidAmount,
        p.status
      ]),
      [],
      ['Subtotal', '', '', 
        yearPayments.reduce((sum, p) => sum + p.amount, 0),
        ''],
      ['Paid', '', '', 
        yearPayments.reduce((sum, p) => sum + p.paidAmount, 0),
        ''],
      ['Remaining', '', '', 
        yearPayments.reduce((sum, p) => sum + (p.amount - p.paidAmount), 0),
        '']
    ];
    
    const sheet = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(workbook, sheet, yearKey);
  });
  
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// Export course scores report
export const exportCourseScoresReport = (course, students, grades, filename) => {
  const doc = new jsPDF();

  try {
    doc.addImage(logo, 'PNG', 14, 10, 20, 20);
  } catch (e) {}

  const studyYearLabel = (() => {
    const rawYear = course?.studyYear?.name || course?.studyYear?.year || course?.yearLabel || course?.studyYearName || 'LEERJAAR 1';
    return String(rawYear || '').trim() || 'LEERJAAR 1';
  })();

  const formatBirthDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('nl-NL');
  };

  doc.setFontSize(16);
  doc.setTextColor(118, 0, 188);
  doc.text('Netherlands Business Academy', 40, 20);

  doc.setFontSize(11);
  doc.setTextColor(50);
  doc.text(studyYearLabel.toUpperCase(), 14, 35);
  doc.text(`Vak: ${course?.name || 'Cursus'} (${course?.code || '-'})`, 14, 43);
  doc.text(`Gegenereerd: ${new Date().toLocaleDateString('nl-NL')}`, 14, 51);

  students.forEach((student, index) => {
    if (index > 0) {
      doc.addPage();
    }

    const studentGrades = (grades || []).filter(
      (g) => String(g.studentId) === String(student.id || student._id) && String(g.courseId) === String(course.id || course._id)
    );

    const studentAverage = studentGrades.length > 0
      ? studentGrades.reduce((sum, g) => sum + Number(g.grade || 0), 0) / studentGrades.length
      : 0;

    const studentCode = student.studentCode || student.studentNumber || student.code || '-';
    const birthDate = formatBirthDate(student.dateOfBirth || student.birthDate);

    const headerY = 20;
    doc.setFontSize(12);
    doc.setTextColor(118, 0, 188);
    doc.text('Naam:', 14, headerY + 18);
    doc.text('Studentcode:', 14, headerY + 26);
    doc.text('Geboorte datum:', 14, headerY + 34);
    doc.text('Totaal te behalen ECTS:', 100, headerY + 18);
    doc.text('ECTS Behaald:', 100, headerY + 26);
    doc.text('Voortgang studie:', 100, headerY + 34);

    doc.setTextColor(50);
    doc.text(String(student.name || '-'), 42, headerY + 18);
    doc.text(String(studentCode), 42, headerY + 26);
    doc.text(String(birthDate), 42, headerY + 34);
    doc.text(String(Number(course?.ects || 0)), 155, headerY + 18);
    doc.text(String(studentGrades.length > 0 ? Number((studentAverage / 10) * Number(course?.ects || 0)).toFixed(1) : '0.0'), 155, headerY + 26);
    doc.text(String(studentGrades.length > 0 ? `${((studentAverage / 10) * 100).toFixed(0)}%` : '0%'), 155, headerY + 34);

    const rows = studentGrades.length > 0
      ? studentGrades.map((grade) => [
          String(grade.examPointKey || grade.examType || 'OND'),
          String(grade.notes || grade.examType || 'Assessment'),
          String(course?.ects || 0),
          String(grade.examType || 'Toets'),
          String(Number(grade.grade || 0).toFixed(1)),
          String(Number(grade.grade || 0).toFixed(1))
        ])
      : [[ '-', course?.name || 'Cursus', String(course?.ects || 0), '-', '-', '-' ]];

    doc.autoTable({
      head: [['CODE', 'MODULE NAAM', 'ECTS', 'TOETSING', 'CIJFER', 'EINDCIJFER']],
      body: rows,
      startY: 62,
      theme: 'grid',
      headStyles: {
        fillColor: [118, 0, 188],
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [50, 50, 50]
      },
      alternateRowStyles: {
        fillColor: [245, 240, 250]
      },
      margin: { left: 14, right: 14 }
    });

    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(10);
    doc.setTextColor(50);
    doc.text(`Gemiddeld: ${Number.isFinite(studentAverage) ? Number(studentAverage).toFixed(1) : '0.0'}`, 14, finalY);
    doc.text(`Status: ${studentAverage >= 5.5 ? 'Voldoende' : 'Onvoldoende'}`, 120, finalY);
  });

  doc.save(`${filename}.pdf`);
};

export const exportCourseScoresExcel = (course, students, grades, filename) => {
  const rows = [];
  const studyYearLabel = course?.studyYear?.name || course?.yearLabel || 'LEERJAAR 1';

  students.forEach((student) => {
    const studentGrades = (grades || []).filter(
      (g) => String(g.studentId) === String(student.id || student._id) && String(g.courseId) === String(course.id || course._id)
    );

    const studentCode = student.studentCode || student.studentNumber || student.code || '-';
    const birthDate = student.dateOfBirth || student.birthDate || '-';
    const average = studentGrades.length > 0
      ? studentGrades.reduce((sum, g) => sum + Number(g.grade || 0), 0) / studentGrades.length
      : 0;

    rows.push({
      'LEERJAAR': studyYearLabel,
      'Naam': student.name || '-',
      'Studentcode': studentCode,
      'Geboorte datum': birthDate,
      'CODE': course?.code || '-',
      'MODULE NAAM': course?.name || '-',
      'ECTS': course?.ects || 0,
      'TOETSING': studentGrades.length > 0 ? (studentGrades[0]?.examType || 'Toets') : '-',
      'CIJFER': studentGrades.length > 0 ? Number(studentGrades[0].grade || 0).toFixed(1) : '0.0',
      'EINDCIJFER': Number.isFinite(average) ? Number(average).toFixed(1) : '0.0',
      'Status': average >= 5.5 ? 'Voldoende' : 'Onvoldoende'
    });
  });

  exportToExcel(rows.length > 0 ? rows : [{
    'LEERJAAR': studyYearLabel,
    'Naam': '-',
    'Studentcode': '-',
    'Geboorte datum': '-',
    'CODE': course?.code || '-',
    'MODULE NAAM': course?.name || '-',
    'ECTS': course?.ects || 0,
    'TOETSING': '-',
    'CIJFER': '0.0',
    'EINDCIJFER': '0.0',
    'Status': 'Onvoldoende'
  }], filename, course?.name || 'Cijferlijst');
};
